/**
 * Consent-gated iframe loader, built on top of BMW ePaaS' official
 * `epaas.api.fallbackpage` bundle (see docs/Consent Controller -
 * Integration.pdf and docs/Consent Controller - JavaScript.pdf).
 *
 * Two integration paths are supported:
 *
 *   1. Iframe placeholders (`.epaas-embed[data-epaas-src]`):
 *      The PHP helper `localmedia_render_consent_iframe()` renders an
 *      empty wrapper that carries the iframe URL on `data-epaas-src`
 *      (no live `src`, so the browser never connects to the third
 *      party before consent). On boot we call
 *      `epaas.api.fallbackpage.then(b => b.handleFallbackPage(
 *          container, url, options))`. ePaaS then either
 *        (a) resolves the promise immediately if consent is already on
 *            file — we inject the iframe with a real `src`, or
 *        (b) renders BMW's audited fallback UI inside the container
 *            and resolves once the user clicks accept.
 *      `consentRejectedCallback` unloads the iframe if the user
 *      revokes consent later.
 *
 *   2. Click-to-load gates (`[data-epaas-gate="<processingId>"]`):
 *      Used for non-iframe triggers like the desktop GLightbox link
 *      that opens a YouTube video in a lightbox. On click, if the
 *      processing is already allowed, we let the click through. If
 *      not, we block the click, render an overlay container next to
 *      the gate, call `handleFallbackPage(container, null,
 *      { processingId })`, and re-fire the click after the promise
 *      resolves so the original action (GLightbox.open) proceeds.
 *
 * Public API used (window.epaas.api):
 *   - registerOnInitialized(cb)
 *   - isInitialized()
 *   - isUsageAllowed(processingId)
 *   - registerOnUserConsentChange(processingId, cb)
 *   - fallbackpage  (lazy-loaded thenable that resolves to the bundle)
 *
 * Set `window.localmediaEpaasDebug = true` for verbose console output.
 */
(function () {
	'use strict';

	if (typeof window !== 'undefined' && window.__localmediaEpaasEmbedLoaded) {
		return;
	}
	if (typeof window !== 'undefined') {
		window.__localmediaEpaasEmbedLoaded = true;
	}

	var SELECTOR              = '.epaas-embed[data-epaas-src]';
	var FALLBACK_CONTAINER_CLASS = 'epaas-embed__fallback';
	var IFRAME_CLASS          = 'epaas-embed__iframe';
	var ACTIVE_CLASS          = 'epaas-embed--active';
	var PLACEHOLDER_REG_ATTR  = 'data-epaas-registered';
	var GATE_RESUMING_PROP    = '__epaasGateResuming';
	var LOG_PREFIX            = '[epaas-embed]';

	function isDebug() {
		return !!(typeof window !== 'undefined' && window.localmediaEpaasDebug);
	}
	function log() {
		if (!isDebug()) { return; }
		if (typeof console === 'undefined' || !console || typeof console.info !== 'function') { return; }
		var args = [LOG_PREFIX];
		for (var i = 0; i < arguments.length; i++) { args.push(arguments[i]); }
		console.info.apply(console, args);
	}
	function logAlways() {
		if (typeof console === 'undefined' || !console || typeof console.log !== 'function') { return; }
		var args = [LOG_PREFIX];
		for (var i = 0; i < arguments.length; i++) { args.push(arguments[i]); }
		console.log.apply(console, args);
	}
	function warnOnce(key, msg) {
		if (!warnOnce._seen) { warnOnce._seen = {}; }
		if (warnOnce._seen[key]) { return; }
		warnOnce._seen[key] = true;
		if (typeof console !== 'undefined' && console && typeof console.warn === 'function') {
			console.warn(LOG_PREFIX, msg);
		}
	}

	function epaas() {
		return (typeof window !== 'undefined' && window.epaas && window.epaas.api) ? window.epaas.api : null;
	}

	function isUsageAllowed(processingId) {
		var api = epaas();
		if (!api || typeof api.isUsageAllowed !== 'function' || !processingId) {
			return false;
		}
		try { return !!api.isUsageAllowed(processingId); } catch (e) { return false; }
	}

	// Resolves with the loaded fallback page bundle, or rejects if the
	// bundle isn't available in this build (e.g. ePaaS misconfigured or
	// blocked by CSP).
	function getFallbackBundle() {
		var api = epaas();
		if (!api) { return Promise.reject(new Error('epaas.api unavailable')); }
		// epaas.api.fallbackpage is a thenable that lazy-loads the bundle.
		// Some integrations expose it as a function; handle both.
		var fp = api.fallbackpage;
		if (!fp) { return Promise.reject(new Error('epaas.api.fallbackpage unavailable')); }
		try {
			if (typeof fp === 'function') { return Promise.resolve(fp()); }
			if (typeof fp.then === 'function') { return Promise.resolve(fp); }
		} catch (e) { /* fall through */ }
		return Promise.reject(new Error('epaas.api.fallbackpage has unexpected shape'));
	}

	// ===== Iframe placeholders =====

	function buildIframe(el) {
		var src   = el.getAttribute('data-epaas-src') || '';
		var title = el.getAttribute('data-epaas-title') || '';
		var allow = el.getAttribute('data-epaas-allow') || '';
		var klass = el.getAttribute('data-epaas-iframe-class') || '';
		if (!src) { return null; }

		var iframe = document.createElement('iframe');
		iframe.src = src;
		if (title) { iframe.setAttribute('title', title); }
		if (allow) { iframe.setAttribute('allow', allow); }
		iframe.setAttribute('allowfullscreen', '');
		iframe.setAttribute('loading', 'lazy');
		iframe.className = (IFRAME_CLASS + ' ' + klass).trim();

		var attrs = el.attributes;
		for (var i = 0; i < attrs.length; i++) {
			var a = attrs[i];
			if (a.name.indexOf('data-epaas-attr-') === 0) {
				var name = a.name.substring('data-epaas-attr-'.length);
				if (name) { iframe.setAttribute(name, a.value); }
			}
		}
		return iframe;
	}

	function ensureFallbackContainer(el) {
		var existing = el.querySelector('.' + FALLBACK_CONTAINER_CLASS);
		if (existing) { return existing; }
		var c = document.createElement('div');
		c.className = FALLBACK_CONTAINER_CLASS;
		el.appendChild(c);
		return c;
	}

	function removeFallbackContainer(el) {
		var c = el.querySelector('.' + FALLBACK_CONTAINER_CLASS);
		if (c && c.parentNode) { c.parentNode.removeChild(c); }
	}

	function removeIframe(el) {
		var iframe = el.querySelector('iframe.' + IFRAME_CLASS);
		if (iframe && iframe.parentNode) { iframe.parentNode.removeChild(iframe); }
	}

	function activatePlaceholder(el) {
		removeFallbackContainer(el);
		if (el.querySelector('iframe.' + IFRAME_CLASS)) {
			el.classList.add(ACTIVE_CLASS);
			return;
		}
		var iframe = buildIframe(el);
		if (iframe) {
			el.appendChild(iframe);
			el.classList.add(ACTIVE_CLASS);
		}
	}

	function deactivatePlaceholder(el) {
		removeIframe(el);
		el.classList.remove(ACTIVE_CLASS);
	}

	function handlePlaceholder(el) {
		if (el.getAttribute(PLACEHOLDER_REG_ATTR) === '1') { return; }
		el.setAttribute(PLACEHOLDER_REG_ATTR, '1');

		var url = el.getAttribute('data-epaas-src');
		var processingId = el.getAttribute('data-epaas-processing-id') || '';
		if (!url) { return; }

		// Fast path: if the explicit processingId is already allowed,
		// inject the iframe directly without invoking the fallback bundle.
		if (processingId && isUsageAllowed(processingId)) {
			activatePlaceholder(el);
			// Still register a consent-change listener so we tear down
			// the iframe if the user revokes consent later.
			registerConsentChangeListener(el, processingId);
			return;
		}

		var container = ensureFallbackContainer(el);
		invokeFallback(container, url, processingId, el);
	}

	function invokeFallback(container, url, processingId, hostEl) {
		getFallbackBundle().then(function (bundle) {
			if (!bundle || typeof bundle.handleFallbackPage !== 'function') {
				warnOnce('fallback-bundle-missing', 'Fallback page bundle loaded but handleFallbackPage is missing — falling back to manual link.');
				renderManualLink(container, url);
				return;
			}
			var options = {
				consentRejectedCallback: function () {
					log('consent rejected -> unloading iframe', url);
					if (hostEl) {
						deactivatePlaceholder(hostEl);
						hostEl.removeAttribute(PLACEHOLDER_REG_ATTR);
						handlePlaceholder(hostEl);
					}
				},
			};

			// For iframe placeholders we always pass the URL and never
			// add processingId to options. The docs say when processingId
			// is in options the bundle ignores the URL and shows a
			// full-viewport fixed overlay (designed for non-iframe content
			// like video players). We want the contained, in-element UI —
			// the URL-based path renders inside the supplied container.
			// (processingId is only used for the gate overlay path.)

			try {
				bundle.handleFallbackPage(container, url, options).then(function () {
					log('fallback resolved -> activating', url);
					if (hostEl) { activatePlaceholder(hostEl); }
				}).catch(function (err) {
					warnOnce('fallback-rejected-' + url, 'ePaaS fallback page rejected for ' + url + ': ' + (err && err.message ? err.message : String(err)));
					renderManualLink(container, url);
				});
			} catch (e) {
				warnOnce('fallback-threw', 'handleFallbackPage threw: ' + String(e));
				renderManualLink(container, url);
			}
		}).catch(function (err) {
			warnOnce('fallback-bundle-load', 'ePaaS fallback bundle could not be loaded: ' + (err && err.message ? err.message : String(err)) + ' — rendering manual fallback link.');
			renderManualLink(container, url);
		});
	}

	// Bare-bones manual fallback shown only if the ePaaS fallback bundle
	// itself fails (network blocked, CSP, etc.). Keeps the page from
	// silently breaking when the official UI can't load.
	function renderManualLink(container, url) {
		if (!container) { return; }
		container.innerHTML = '';
		var p = document.createElement('p');
		p.textContent = 'Externer Inhalt blockiert. ';
		var a = document.createElement('a');
		a.href = url;
		a.target = '_blank';
		a.rel = 'noopener noreferrer';
		a.textContent = 'In neuem Tab öffnen';
		p.appendChild(a);
		container.appendChild(p);
		container.classList.add(FALLBACK_CONTAINER_CLASS + '--manual');
	}

	function registerConsentChangeListener(el, processingId) {
		var api = epaas();
		if (!api || typeof api.registerOnUserConsentChange !== 'function' || !processingId) { return; }
		try {
			api.registerOnUserConsentChange(processingId, function () {
				if (!isUsageAllowed(processingId)) {
					log('consent revoked -> unloading', processingId);
					deactivatePlaceholder(el);
					el.removeAttribute(PLACEHOLDER_REG_ATTR);
					handlePlaceholder(el);
				}
			});
		} catch (e) { /* noop */ }
	}

	function initAllPlaceholders() {
		var nodes = document.querySelectorAll(SELECTOR);
		for (var i = 0; i < nodes.length; i++) { handlePlaceholder(nodes[i]); }
	}

	// ===== Click-to-load gates =====

	function findGate(node) {
		if (!node || typeof node.closest !== 'function') { return null; }
		return node.closest('[data-epaas-gate]');
	}

	function installGateDelegation() {
		if (installGateDelegation._installed) { return; }
		installGateDelegation._installed = true;
		document.addEventListener('click', delegatedGateHandler, true);
		log('gate delegation installed (capture phase)');
	}

	function delegatedGateHandler(ev) {
		var gate = findGate(ev.target);
		if (!gate) { return; }
		var processingId = gate.getAttribute('data-epaas-gate');
		if (!processingId) { return; }

		if (gate[GATE_RESUMING_PROP]) {
			gate[GATE_RESUMING_PROP] = false;
			return;
		}

		if (isUsageAllowed(processingId)) { return; }

		ev.preventDefault();
		ev.stopPropagation();
		if (typeof ev.stopImmediatePropagation === 'function') {
			ev.stopImmediatePropagation();
		}

		log('gate blocked', processingId, gate);
		handleGateClick(gate, processingId);
	}

	function handleGateClick(gate, processingId) {
		if (gate.__epaasGateBusy) { return; }
		gate.__epaasGateBusy = true;

		var overlay = createGateOverlay(gate);
		getFallbackBundle().then(function (bundle) {
			if (!bundle || typeof bundle.handleFallbackPage !== 'function') {
				warnOnce('fallback-bundle-missing', 'Fallback page bundle missing for gate ' + processingId);
				cleanupOverlay(overlay);
				gate.__epaasGateBusy = false;
				return;
			}
			var options = {
				processingId: processingId,
				consentRejectedCallback: function () { /* handled by reactive listener if added */ },
			};
			try {
				bundle.handleFallbackPage(overlay, null, options).then(function () {
					cleanupOverlay(overlay);
					gate.__epaasGateBusy = false;
					gate[GATE_RESUMING_PROP] = true;
					try { gate.click(); } catch (e) { gate[GATE_RESUMING_PROP] = false; }
				}).catch(function (err) {
					warnOnce('gate-fallback-rejected-' + processingId, 'Gate fallback page rejected for ' + processingId + ': ' + (err && err.message ? err.message : String(err)));
					cleanupOverlay(overlay);
					gate.__epaasGateBusy = false;
				});
			} catch (e) {
				warnOnce('gate-fallback-threw-' + processingId, 'handleFallbackPage threw for gate ' + processingId);
				cleanupOverlay(overlay);
				gate.__epaasGateBusy = false;
			}
		}).catch(function (err) {
			warnOnce('gate-bundle-load-' + processingId, 'ePaaS fallback bundle could not be loaded for gate ' + processingId + ': ' + (err && err.message ? err.message : String(err)));
			cleanupOverlay(overlay);
			gate.__epaasGateBusy = false;
		});
	}

	// Renders an overlay that covers the gate's nearest positioned
	// ancestor (e.g. the video thumbnail wrapper). Appending it inside
	// that ancestor as `position: absolute` (instead of `fixed` on the
	// body) keeps the fallback UI pinned over the element it covers and
	// scrolling with the page. `transform: translateZ(0)` makes the
	// overlay the containing block for any `position: fixed` children
	// the BMW bundle injects, so they stay contained too.
	function findPositionedAncestor(node) {
		var el = node && node.parentElement;
		while (el && el !== document.body) {
			var pos = window.getComputedStyle(el).position;
			if (pos === 'relative' || pos === 'absolute' || pos === 'fixed' || pos === 'sticky') {
				return el;
			}
			el = el.parentElement;
		}
		return (node && node.parentElement) || document.body;
	}

	function createGateOverlay(gate) {
		var host = findPositionedAncestor(gate);
		var overlay = document.createElement('div');
		overlay.className = 'epaas-gate-overlay';
		overlay.style.cssText = [
			'position:absolute',
			'inset:0',
			'z-index:50',
			'background:#fff',
			'border-radius:4px',
			'overflow:auto',
			'transform:translateZ(0)',
		].join(';');
		host.appendChild(overlay);
		return overlay;
	}

	function cleanupOverlay(overlay) {
		if (overlay && overlay.parentNode) { overlay.parentNode.removeChild(overlay); }
	}

	// ===== Boot =====

	function boot() {
		installGateDelegation();
		initAllPlaceholders();
	}

	function whenDom(fn) {
		if (document.readyState === 'loading') {
			document.addEventListener('DOMContentLoaded', fn);
		} else {
			fn();
		}
	}

	whenDom(function () {
		logAlways('script loaded (build 2026-06-05 v5 — fallbackpage bundle).');
		boot();

		var api = epaas();
		if (api && typeof api.registerOnInitialized === 'function') {
			try { api.registerOnInitialized(function () { boot(); }); } catch (e) { /* */ }
		} else {
			// epaas.js may not be present yet — poll briefly.
			var tries = 0;
			var iv = setInterval(function () {
				tries++;
				var late = epaas();
				if (late) {
					clearInterval(iv);
					if (typeof late.registerOnInitialized === 'function') {
						try { late.registerOnInitialized(function () { boot(); }); } catch (e) { /* */ }
					}
					boot();
				} else if (tries > 100) {
					clearInterval(iv);
				}
			}, 100);
		}
	});

	// ===== Console diagnostics =====

	if (typeof window !== 'undefined') {
		// Quick smoke test: report which placeholders / gates are on the
		// page and whether the fallback bundle can be loaded. Use this
		// from the dev console when verifying integrations.
		window.localmediaEpaasInspect = function () {
			var api = epaas();
			var out = {
				epaasLoaded:   !!api,
				isInitialized: !!(api && typeof api.isInitialized === 'function' && api.isInitialized()),
				placeholders:  [],
				gates:         [],
				fallbackBundleAvailable: !!(api && api.fallbackpage),
			};
			var ph = document.querySelectorAll(SELECTOR);
			for (var i = 0; i < ph.length; i++) {
				out.placeholders.push({
					url:          ph[i].getAttribute('data-epaas-src'),
					processingId: ph[i].getAttribute('data-epaas-processing-id') || null,
					active:       ph[i].classList.contains(ACTIVE_CLASS),
				});
			}
			var gates = document.querySelectorAll('[data-epaas-gate]');
			for (var j = 0; j < gates.length; j++) {
				var pid = gates[j].getAttribute('data-epaas-gate');
				out.gates.push({
					processingId: pid,
					isAllowed:    isUsageAllowed(pid),
				});
			}
			return out;
		};
	}
})();
